import { useState } from 'react';
import { useTheme, useMediaQuery } from '@mui/material';
import { getIngestUrl, authFetch } from '../config';
import SubPageHeader from '../components/SubPageHeader';

const API_BASE = getIngestUrl('/blocklist');

export default function BlocklistImport() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [step, setStep] = useState<'upload' | 'mapping' | 'matching' | 'review'>('upload');
  const [sourceId, setSourceId] = useState<number | null>(null);
  const [columns, setColumns] = useState<any[]>([]);
  const [mapping, setMapping] = useState<Record<string, string>>({});
  const [matchStats, setMatchStats] = useState<any>(null);
  const [records, setRecords] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [blocklistType, setBlocklistType] = useState<'robinson' | 'widerspruch' | 'custom'>('robinson');

  const coreFields = [
    { value: '', label: 'Nicht zuordnen' },
    { value: 'anrede', label: 'Anrede' },
    { value: 'vorname', label: 'Vorname' },
    { value: 'nachname', label: 'Nachname' },
    { value: 'strasse', label: 'Straße' },
    { value: 'haus_nr', label: 'Hausnummer' },
    { value: 'plz', label: 'PLZ' },
    { value: 'ort', label: 'Ort' },
    { value: 'email', label: 'E-Mail' },
    { value: 'telefon', label: 'Telefon' },
  ];

  const blocklistTypes = [
    { value: 'robinson', label: 'Robinson-Liste', color: '#dc2626' },
    { value: 'widerspruch', label: 'Widerspruch', color: '#ea580c' },
    { value: 'custom', label: 'Benutzerdefiniert', color: '#9333ea' },
  ];

  const handleMappingChange = (columnName: string, targetField: string) => {
    const newMapping = { ...mapping };
    if (targetField) {
      newMapping[columnName] = targetField;
    } else {
      delete newMapping[columnName];
    }
    setMapping(newMapping);
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setLoading(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('source_name', file.name);
    formData.append('blocklist_type', blocklistType);
    
    try {
      const response = await authFetch(`${API_BASE}/upload`, {
        method: 'POST',
        body: formData
      });
      
      const data = await response.json();
      
      if (data.success) {
        setSourceId(data.data.source_id);
        setColumns(data.data.columns);
        
        const initialMapping: Record<string, string> = {};
        data.data.columns.forEach((col: any) => {
          if (col.suggested_mapping) {
            initialMapping[col.name] = col.suggested_mapping;
          }
        });
        setMapping(initialMapping);
        setStep('mapping');
      } else {
        alert('Upload fehlgeschlagen: ' + data.error);
      }
    } catch (err) {
      alert('Fehler beim Upload: ' + (err as Error).message);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveMapping = async () => {
    setLoading(true);
    try {
      await authFetch(`${API_BASE}/${sourceId}/mapping`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mapping })
      });
      
      setStep('matching');
      await handleStartMatching();
    } catch (err) {
      alert('Fehler beim Mapping: ' + (err as Error).message);
    } finally {
      setLoading(false);
    }
  };

  const handleStartMatching = async () => {
    const response = await authFetch(`${API_BASE}/${sourceId}/match`, { method: 'POST' });
    const data = await response.json();
    
    if (data.success) {
      setMatchStats(data.data);
      await loadRecords();
      setStep('review');
    }
  };

  const loadRecords = async () => {
    const response = await authFetch(`${API_BASE}/${sourceId}/records?status=all`);
    const data = await response.json();
    setRecords(Array.isArray(data.records) ? data.records : [data.records]);
  };

  const handleBlock = async () => {
    if (!window.confirm(`${matchStats?.matched || 0} Spieler sperren?\n\nDiese Spieler werden für zukünftige Kampagnen gesperrt.`)) return;

    setLoading(true);
    try {
      const response = await authFetch(`${API_BASE}/${sourceId}/block`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      
      const data = await response.json();
      if (data.success) {
        alert(`Erfolgreich gesperrt: ${data.blocked_count} Spieler`);
        setStep('upload');
        setSourceId(null);
        setColumns([]);
        setMapping({});
        setMatchStats(null);
        setRecords([]);
      }
    } catch (err) {
      alert('Sperrung fehlgeschlagen: ' + (err as Error).message);
    } finally {
      setLoading(false);
    }
  };

  const selectedType = blocklistTypes.find(t => t.value === blocklistType);

  const steps = [
    { key: 'upload', label: 'Upload' },
    { key: 'mapping', label: 'Mapping' },
    { key: 'matching', label: 'Matching' },
    { key: 'review', label: 'Review' }
  ];

  const currentStepIndex = steps.findIndex(s => s.key === step);

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: '#f8f9fa',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
    }}>
      <SubPageHeader title="Sperrliste hochladen" />

      {/* Progress Steps */}
      <div style={{ backgroundColor: 'white', borderBottom: '1px solid #e5e7eb' }}>
        <div style={{ 
          maxWidth: '1200px', 
          margin: '0 auto', 
          padding: isMobile ? '1rem' : '1.5rem 2rem',
          overflowX: 'auto'
        }}>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            minWidth: isMobile ? '600px' : 'auto'
          }}>
            {steps.map((s, idx) => (
              <div key={s.key} style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                <div style={{
                  width: isMobile ? '28px' : '32px',
                  height: isMobile ? '28px' : '32px',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: step === s.key ? '#dc2626' : currentStepIndex > idx ? '#10b981' : '#e5e7eb',
                  color: step === s.key || currentStepIndex > idx ? 'white' : '#6b7280',
                  fontWeight: '600',
                  fontSize: isMobile ? '0.8rem' : '0.875rem',
                  flexShrink: 0
                }}>
                  {idx + 1}
                </div>
                <div style={{ 
                  marginLeft: isMobile ? '0.5rem' : '0.75rem', 
                  fontSize: isMobile ? '0.85rem' : '0.95rem', 
                  fontWeight: '500', 
                  color: step === s.key ? '#dc2626' : '#6b7280',
                  whiteSpace: 'nowrap'
                }}>
                  {s.label}
                </div>
                {idx < 3 && <div style={{ flex: 1, height: '2px', backgroundColor: '#e5e7eb', marginLeft: isMobile ? '0.5rem' : '1rem' }}></div>}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main style={{ 
        maxWidth: '1200px', 
        margin: isMobile ? '1rem auto' : '2rem auto', 
        padding: isMobile ? '0 1rem' : '0 2rem' 
      }}>
        {step === 'upload' && (
          <div style={{ 
            backgroundColor: 'white', 
            padding: isMobile ? '1.5rem' : '2rem', 
            borderRadius: '8px', 
            boxShadow: '0 1px 3px rgba(0,0,0,0.12)', 
            border: '1px solid #e5e7eb' 
          }}>
            <h2 style={{ 
              margin: '0 0 1rem 0', 
              color: '#dc2626', 
              fontSize: isMobile ? '1.25rem' : '1.5rem', 
              fontWeight: '600' 
            }}>
              Sperrliste hochladen
            </h2>
            <p style={{ 
              margin: '0 0 1.5rem 0', 
              color: '#6b7280',
              fontSize: isMobile ? '0.9rem' : '1rem'
            }}>
              CSV oder Excel-Datei mit Personen, die für Kampagnen gesperrt werden sollen
            </p>

            {/* Blocklist Type Selection */}
            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ 
                display: 'block', 
                marginBottom: '0.5rem', 
                fontWeight: '500', 
                color: '#374151',
                fontSize: isMobile ? '0.9rem' : '1rem'
              }}>
                Art der Sperrliste
              </label>
              <div style={{ 
                display: 'flex', 
                flexDirection: isSmallMobile ? 'column' : 'row',
                gap: '0.75rem' 
              }}>
                {blocklistTypes.map(type => (
                  <button
                    key={type.value}
                    onClick={() => setBlocklistType(type.value as any)}
                    style={{
                      flex: 1,
                      padding: isMobile ? '0.85rem' : '0.75rem 1rem',
                      border: blocklistType === type.value ? `2px solid ${type.color}` : '2px solid #e5e7eb',
                      borderRadius: '6px',
                      backgroundColor: blocklistType === type.value ? `${type.color}10` : 'white',
                      color: blocklistType === type.value ? type.color : '#6b7280',
                      fontWeight: '500',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      fontSize: isMobile ? '0.9rem' : '1rem'
                    }}
                  >
                    {type.label}
                  </button>
                ))}
              </div>
              <div style={{ 
                marginTop: '0.75rem', 
                padding: '0.75rem', 
                backgroundColor: `${selectedType?.color}10`,
                borderRadius: '6px',
                fontSize: isMobile ? '0.8rem' : '0.875rem',
                color: '#6b7280'
              }}>
                {blocklistType === 'robinson' && '📋 Robinson-Liste: Personen, die generell keine Werbung wünschen'}
                {blocklistType === 'widerspruch' && '🚫 Widerspruch: Personen mit explizitem Widerspruch'}
                {blocklistType === 'custom' && '⚙️ Benutzerdefiniert: Andere Sperrungsgründe'}
              </div>
            </div>

            <input
              type="file"
              accept=".csv,.xlsx,.xls"
              onChange={handleFileUpload}
              disabled={loading}
              style={{
                display: 'block',
                width: '100%',
                padding: isMobile ? '0.85rem' : '1rem',
                border: '2px dashed #d1d5db',
                borderRadius: '6px',
                cursor: loading ? 'not-allowed' : 'pointer',
                backgroundColor: '#f9fafb',
                fontSize: isMobile ? '0.9rem' : '1rem'
              }}
            />
            {loading && <p style={{ marginTop: '1rem', color: '#dc2626', fontSize: isMobile ? '0.9rem' : '1rem' }}>Wird hochgeladen...</p>}
          </div>
        )}

        {step === 'mapping' && (
          <div style={{ 
            backgroundColor: 'white', 
            padding: isMobile ? '1.5rem' : '2rem', 
            borderRadius: '8px', 
            boxShadow: '0 1px 3px rgba(0,0,0,0.12)', 
            border: '1px solid #e5e7eb' 
          }}>
            <h2 style={{ 
              margin: '0 0 1rem 0', 
              color: '#dc2626', 
              fontSize: isMobile ? '1.25rem' : '1.5rem', 
              fontWeight: '600' 
            }}>
              Spalten zuordnen
            </h2>
            <p style={{ 
              margin: '0 0 1.5rem 0', 
              color: '#6b7280',
              fontSize: isMobile ? '0.9rem' : '1rem'
            }}>
              Ordnen Sie die Spalten zu, um Personen identifizieren zu können
            </p>
            <div style={{ 
              display: 'flex', 
              flexDirection: 'column', 
              gap: '0.75rem', 
              marginBottom: '1.5rem' 
            }}>
              {columns.map((col) => (
                <div key={col.name} style={{
                  display: 'flex',
                  flexDirection: isMobile ? 'column' : 'row',
                  alignItems: isMobile ? 'stretch' : 'center',
                  gap: isMobile ? '0.75rem' : '1rem',
                  padding: isMobile ? '0.85rem' : '1rem',
                  backgroundColor: '#f9fafb',
                  borderRadius: '6px',
                  border: '1px solid #e5e7eb'
                }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ 
                      fontWeight: '500', 
                      color: '#111827',
                      fontSize: isMobile ? '0.9rem' : '1rem'
                    }}>
                      {col.name}
                    </div>
                    <div style={{ 
                      fontSize: isMobile ? '0.8rem' : '0.875rem', 
                      color: '#6b7280', 
                      marginTop: '0.25rem',
                      wordBreak: 'break-all'
                    }}>
                      {col.sample_values.join(', ')}
                    </div>
                  </div>
                  <select
                    value={mapping[col.name] || ''}
                    onChange={(e) => handleMappingChange(col.name, e.target.value)}
                    style={{
                      padding: '0.5rem 0.75rem',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      backgroundColor: 'white',
                      fontSize: isMobile ? '0.9rem' : '0.95rem',
                      minWidth: isMobile ? 'auto' : '200px',
                      width: isMobile ? '100%' : 'auto'
                    }}
                  >
                    {coreFields.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
                  </select>
                </div>
              ))}
            </div>
            <button
              onClick={handleSaveMapping}
              disabled={loading}
              style={{
                padding: isMobile ? '0.85rem 1.25rem' : '0.75rem 1.5rem',
                backgroundColor: loading ? '#9ca3af' : '#dc2626',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: loading ? 'not-allowed' : 'pointer',
                fontSize: isMobile ? '0.95rem' : '1rem',
                fontWeight: '500',
                width: isMobile ? '100%' : 'auto'
              }}
            >
              {loading ? 'Verarbeite...' : 'Weiter zum Matching'}
            </button>
          </div>
        )}

        {step === 'matching' && (
          <div style={{
            textAlign: 'center',
            padding: isMobile ? '3rem 1.5rem' : '4rem 2rem',
            backgroundColor: 'white',
            borderRadius: '8px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
            border: '1px solid #e5e7eb'
          }}>
            <div style={{
              width: isMobile ? '48px' : '64px',
              height: isMobile ? '48px' : '64px',
              border: '4px solid #e5e7eb',
              borderTopColor: '#dc2626',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              margin: '0 auto 1.5rem'
            }}></div>
            <h3 style={{ 
              margin: '0 0 0.5rem 0', 
              color: '#dc2626', 
              fontSize: isMobile ? '1.1rem' : '1.25rem', 
              fontWeight: '600' 
            }}>
              Matching läuft...
            </h3>
            <p style={{ 
              margin: 0, 
              color: '#6b7280',
              fontSize: isMobile ? '0.9rem' : '1rem'
            }}>
              Personen werden gegen die Spielerdatenbank abgeglichen
            </p>
          </div>
        )}

        {step === 'review' && (
          <div>
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)', 
              gap: '1rem', 
              marginBottom: '1.5rem' 
            }}>
              <div style={{ 
                backgroundColor: 'white', 
                padding: isMobile ? '1.25rem' : '1.5rem', 
                borderRadius: '8px', 
                boxShadow: '0 1px 3px rgba(0,0,0,0.12)', 
                border: '1px solid #e5e7eb' 
              }}>
                <div style={{ 
                  fontSize: isMobile ? '1.75rem' : '2rem', 
                  fontWeight: 'bold', 
                  color: '#dc2626' 
                }}>
                  {matchStats?.matched || 0}
                </div>
                <div style={{ 
                  fontSize: isMobile ? '0.8rem' : '0.875rem', 
                  color: '#6b7280', 
                  marginTop: '0.25rem' 
                }}>
                  Spieler gefunden
                </div>
              </div>
              <div style={{ 
                backgroundColor: 'white', 
                padding: isMobile ? '1.25rem' : '1.5rem', 
                borderRadius: '8px', 
                boxShadow: '0 1px 3px rgba(0,0,0,0.12)', 
                border: '1px solid #e5e7eb' 
              }}>
                <div style={{ 
                  fontSize: isMobile ? '1.75rem' : '2rem', 
                  fontWeight: 'bold', 
                  color: '#6b7280' 
                }}>
                  {matchStats?.unmatched || 0}
                </div>
                <div style={{ 
                  fontSize: isMobile ? '0.8rem' : '0.875rem', 
                  color: '#6b7280', 
                  marginTop: '0.25rem' 
                }}>
                  Nicht gefunden
                </div>
              </div>
              <div style={{ 
                backgroundColor: 'white', 
                padding: isMobile ? '1.25rem' : '1.5rem', 
                borderRadius: '8px', 
                boxShadow: '0 1px 3px rgba(0,0,0,0.12)', 
                border: '1px solid #e5e7eb' 
              }}>
                <div style={{ 
                  fontSize: isMobile ? '1.75rem' : '2rem', 
                  fontWeight: 'bold', 
                  color: '#111827' 
                }}>
                  {matchStats?.total || 0}
                </div>
                <div style={{ 
                  fontSize: isMobile ? '0.8rem' : '0.875rem', 
                  color: '#6b7280', 
                  marginTop: '0.25rem' 
                }}>
                  Gesamt
                </div>
              </div>
            </div>

            {/* Warning Box */}
            <div style={{
              backgroundColor: '#fef2f2',
              border: '1px solid #fecaca',
              borderRadius: '8px',
              padding: isMobile ? '0.85rem' : '1rem',
              marginBottom: '1.5rem',
              display: 'flex',
              gap: '0.75rem'
            }}>
              <span style={{ fontSize: isMobile ? '1.25rem' : '1.5rem' }}>⚠️</span>
              <div>
                <div style={{ 
                  fontWeight: '600', 
                  color: '#991b1b', 
                  marginBottom: '0.25rem',
                  fontSize: isMobile ? '0.9rem' : '1rem'
                }}>
                  Achtung
                </div>
                <div style={{ 
                  fontSize: isMobile ? '0.8rem' : '0.875rem', 
                  color: '#7f1d1d' 
                }}>
                  Die gefundenen Spieler werden gesperrt und können nicht mehr für Kampagnen verwendet werden.
                  Diese Aktion kann später rückgängig gemacht werden.
                </div>
              </div>
            </div>

            <div style={{ 
              backgroundColor: 'white', 
              borderRadius: '8px', 
              boxShadow: '0 1px 3px rgba(0,0,0,0.12)', 
              border: '1px solid #e5e7eb', 
              overflowX: 'auto' 
            }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: isMobile ? '600px' : 'auto' }}>
                <thead style={{ backgroundColor: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                  <tr>
                    <th style={{ 
                      padding: isMobile ? '0.65rem 0.85rem' : '0.75rem 1rem', 
                      textAlign: 'left', 
                      fontSize: isMobile ? '0.8rem' : '0.875rem', 
                      fontWeight: '600', 
                      color: '#374151' 
                    }}>
                      Status
                    </th>
                    <th style={{ 
                      padding: isMobile ? '0.65rem 0.85rem' : '0.75rem 1rem', 
                      textAlign: 'left', 
                      fontSize: isMobile ? '0.8rem' : '0.875rem', 
                      fontWeight: '600', 
                      color: '#374151' 
                    }}>
                      Sperrliste
                    </th>
                    <th style={{ 
                      padding: isMobile ? '0.65rem 0.85rem' : '0.75rem 1rem', 
                      textAlign: 'left', 
                      fontSize: isMobile ? '0.8rem' : '0.875rem', 
                      fontWeight: '600', 
                      color: '#374151' 
                    }}>
                      Gefundener Spieler
                    </th>
                    <th style={{ 
                      padding: isMobile ? '0.65rem 0.85rem' : '0.75rem 1rem', 
                      textAlign: 'left', 
                      fontSize: isMobile ? '0.8rem' : '0.875rem', 
                      fontWeight: '600', 
                      color: '#374151' 
                    }}>
                      Match-Score
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {records.map((r) => (
                    <tr key={r.id} style={{ borderTop: '1px solid #e5e7eb' }}>
                      <td style={{ padding: isMobile ? '0.65rem 0.85rem' : '0.75rem 1rem' }}>
                        <span style={{
                          padding: '0.25rem 0.75rem',
                          fontSize: isMobile ? '0.7rem' : '0.75rem',
                          borderRadius: '9999px',
                          fontWeight: '500',
                          backgroundColor: r.matched_player_id ? '#fee2e2' : '#f3f4f6',
                          color: r.matched_player_id ? '#991b1b' : '#6b7280',
                          whiteSpace: 'nowrap'
                        }}>
                          {r.matched_player_id ? '🚫 Wird gesperrt' : 'Nicht gefunden'}
                        </span>
                      </td>
                      <td style={{ padding: isMobile ? '0.65rem 0.85rem' : '0.75rem 1rem' }}>
                        <div style={{ 
                          fontWeight: '500', 
                          color: '#111827',
                          fontSize: isMobile ? '0.85rem' : '1rem'
                        }}>
                          {r.vorname} {r.nachname}
                        </div>
                        <div style={{ 
                          fontSize: isMobile ? '0.8rem' : '0.875rem', 
                          color: '#6b7280', 
                          marginTop: '0.25rem' 
                        }}>
                          {r.plz} {r.ort}
                        </div>
                      </td>
                      <td style={{ padding: isMobile ? '0.65rem 0.85rem' : '0.75rem 1rem' }}>
                        {r.matched_player_id ? (
                          <div>
                            <div style={{ 
                              fontWeight: '500', 
                              color: '#111827',
                              fontSize: isMobile ? '0.85rem' : '1rem'
                            }}>
                              {r.player_vorname} {r.player_nachname}
                            </div>
                            <div style={{ 
                              fontSize: isMobile ? '0.8rem' : '0.875rem', 
                              color: '#6b7280', 
                              marginTop: '0.25rem' 
                            }}>
                              Kunden-Nr: {r.player_kunden_nr} • {r.player_plz} {r.player_ort}
                            </div>
                          </div>
                        ) : <span style={{ color: '#9ca3af' }}>-</span>}
                      </td>
                      <td style={{ padding: isMobile ? '0.65rem 0.85rem' : '0.75rem 1rem' }}>
                        {r.matched_player_id ? (
                          <span style={{ 
                            fontWeight: '500', 
                            color: '#111827',
                            fontSize: isMobile ? '0.85rem' : '1rem'
                          }}>
                            {r.match_score}%
                          </span>
                        ) : (
                          <span style={{ color: '#9ca3af' }}>-</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div style={{ 
              display: 'flex', 
              flexDirection: isMobile ? 'column' : 'row',
              gap: '1rem', 
              marginTop: '1.5rem' 
            }}>
              <button
                onClick={() => setStep('mapping')}
                style={{
                  padding: isMobile ? '0.85rem 1.25rem' : '0.75rem 1.5rem',
                  backgroundColor: 'white',
                  color: '#374151',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: isMobile ? '0.95rem' : '1rem',
                  fontWeight: '500',
                  width: isMobile ? '100%' : 'auto'
                }}
              >
                Zurück zum Mapping
              </button>
              <button
                onClick={handleBlock}
                disabled={loading || !matchStats?.matched}
                style={{
                  flex: 1,
                  padding: isMobile ? '0.85rem 1.25rem' : '0.75rem 1.5rem',
                  backgroundColor: (loading || !matchStats?.matched) ? '#9ca3af' : '#dc2626',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: (loading || !matchStats?.matched) ? 'not-allowed' : 'pointer',
                  fontSize: isMobile ? '0.95rem' : '1rem',
                  fontWeight: '500'
                }}
              >
                {loading ? 'Wird gesperrt...' : `${matchStats?.matched || 0} Spieler sperren`}
              </button>
            </div>
          </div>
        )}
      </main>

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}
